import { useState } from "react";
import { RotateCcw, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";

const periods = [
    { key: "today", label: "Hari ini" },
    { key: "month", label: "Bulan ini" },
    { key: "allTime", label: "Sepanjang waktu" },
];

// The API returns integer strings: converting to Number can round financial totals.
export const formatProfitIDR = (value) => "Rp " + BigInt(value).toLocaleString("id-ID");

export default function OwnerProfitPanel({ data, loading, error, onRefresh }) {
    const [period, setPeriod] = useState("month");
    const selected = data?.[period];

    return (
        <section aria-labelledby="owner-profit-heading" aria-busy={loading} className="rounded-2xl bg-card border border-border overflow-hidden">
            <div className="p-5 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5 text-brand-deep dark:text-brand-green" aria-hidden="true" />
                        <h2 id="owner-profit-heading" className="font-heading font-bold text-lg">Keuntungan Owner (Laba Kotor)</h2>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">Margin transaksi sukses seluruh pengguna. Periode mengikuti waktu sukses, WIB.</p>
                </div>
                <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} className="self-start shrink-0">
                    <RotateCcw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
                    {loading ? "Memuat keuntungan..." : "Segarkan keuntungan"}
                </Button>
            </div>

            {loading ? (
                <div role="status" className="px-5 pb-5">
                    <span className="sr-only">Memuat ringkasan keuntungan owner</span>
                    <div className="h-36 rounded-xl bg-muted animate-pulse" />
                </div>
            ) : error ? (
                <div role="alert" className="mx-5 mb-5 rounded-xl border border-destructive/40 bg-destructive/5 p-4">
                    <p className="font-semibold text-sm">Keuntungan belum dapat dimuat</p>
                    <p className="text-sm text-muted-foreground mt-1">{error}</p>
                    <Button variant="outline" size="sm" className="mt-3" onClick={onRefresh}>Coba lagi</Button>
                </div>
            ) : data ? (
                <>
                    <div className="grid gap-3 sm:grid-cols-3 px-5 pb-5" role="group" aria-label="Periode keuntungan">
                        {periods.map(({ key, label }) => (
                            <button key={key} type="button" aria-pressed={period === key} onClick={() => setPeriod(key)}
                                className={`min-w-0 text-left rounded-xl border p-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-green focus-visible:ring-offset-2 focus-visible:ring-offset-background ${period === key ? "border-brand-deep bg-brand-deep text-white dark:border-brand-green dark:bg-brand-green dark:text-brand-deeper" : "border-border hover:bg-muted/50"}`}>
                                <span className="block text-xs font-semibold">{label}</span>
                                <span className="block mt-2 font-heading font-extrabold text-xl lg:text-2xl break-words tabular-nums">{formatProfitIDR(data[key].profit)}</span>
                                <span className={`block mt-2 text-xs ${period === key ? "opacity-90" : "text-muted-foreground"}`}>{data[key].transactions.toLocaleString("id-ID")} transaksi sukses</span>
                            </button>
                        ))}
                    </div>
                    <div className="border-t border-border bg-muted/20 px-5 py-4" aria-live="polite">
                        <p className="text-xs font-semibold mb-3">Rincian {periods.find(({ key }) => key === period).label.toLowerCase()}</p>
                        <dl className="grid gap-3 sm:grid-cols-3 text-sm">
                            <div><dt className="text-muted-foreground">Penjualan</dt><dd className="font-semibold mt-1 break-words tabular-nums">{formatProfitIDR(selected.revenue)}</dd></div>
                            <div><dt className="text-muted-foreground">Modal provider</dt><dd className="font-semibold mt-1 break-words tabular-nums">{formatProfitIDR(selected.cost)}</dd></div>
                            <div><dt className="text-muted-foreground">Laba kotor</dt><dd className="font-semibold mt-1 break-words tabular-nums">{formatProfitIDR(selected.profit)}</dd></div>
                        </dl>
                        {selected.transactions === 0 && <p className="text-sm text-muted-foreground mt-4">Belum ada transaksi sukses pada periode ini.</p>}
                    </div>
                </>
            ) : null}

            <div className="border-t border-border px-5 py-4 text-xs text-muted-foreground leading-relaxed">
                <p>Laba kotor = penjualan dikurangi modal provider, sebelum biaya operasional. Ini bukan saldo dompet atau keuntungan pribadi mitra.</p>
                <p className="mt-1">Hanya transaksi berstatus sukses yang dihitung; transaksi menunggu, gagal, dan refund tidak termasuk. Nilai memakai harga saat transaksi, bukan harga produk saat ini.</p>
                {data && !loading && !error && <p className="mt-2">Diperbarui {new Date(data.generatedAt).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} WIB</p>}
            </div>
        </section>
    );
}
